/**
 * Maps API error objects to human-readable strings for display in notifications.
 * Works with the custom APIClient error shape (status, code, message) as well as
 * generic Error objects and unknown error types.
 */

interface ApiErrorLike {
  status?: number;
  code?: string;
  message?: string;
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have permission to perform this action.",
  404: 'The requested resource was not found.',
  408: 'The request timed out. Please try again.',
  409: 'A conflict occurred. Someone else may have updated this resource.',
  422: 'The submitted data is invalid. Please review and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An internal server error occurred. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
  504: 'The server took too long to respond. Please try again.',
};

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Converts any error object into a user-readable string.
 *
 * Handles:
 * - API errors with numeric `status` codes (from the custom APIClient)
 * - Network errors (no `status` property)
 * - Unknown/null/undefined error values
 *
 * Never returns empty strings, raw stack traces, `[object Object]`, or internal codes.
 */
export function formatApiError(error: unknown): string {
  if (error == null) {
    return DEFAULT_MESSAGE;
  }

  const err = error as ApiErrorLike;

  // Map known HTTP status codes to readable messages
  if (typeof err.status === 'number' && STATUS_MESSAGES[err.status]) {
    return STATUS_MESSAGES[err.status];
  }

  // Network errors (no status) — e.g. offline, DNS failure, CORS
  if (err.message && !err.status) {
    if (err.message.toLowerCase().includes('network') || err.message.toLowerCase().includes('fetch')) {
      return 'A network error occurred. Please check your connection and try again.';
    }
    if (err.message.toLowerCase().includes('timeout') || err.message.toLowerCase().includes('aborted')) {
      return 'The request timed out. Please try again.';
    }
  }

  return DEFAULT_MESSAGE;
}
